<template>
  <div>
    <Head title="Pertanggung Jawaban" />
    <h1 class="mb-8 text-2xl font-bold">Pertanggung Jawaban</h1>

    <!-- Tombol Tambah -->
    <div class="flex items-center justify-end mb-6">
      <Link
        v-if="can('write permohonan beasiswa')"
        class="btn-indigo"
        href="/accountability-reports/create"
      >
        <span><font-awesome-icon icon="fas fa-plus" class="mr-2" />Tambah</span>
      </Link>
    </div>

    <!-- WRAPPER RESPONSIVE -->
    <div class="w-full bg-white rounded-md shadow">
      <!-- DESKTOP TABLE -->
      <div class="hidden overflow-x-auto sm:block">
        <table class="w-full text-sm border-collapse">
          <thead class="bg-gray-100">
            <tr class="font-bold text-left border border-gray-300">
              <th class="px-4 py-3 border border-gray-300">Periode Beasiswa</th>
              <th class="px-4 py-3 border border-gray-300">Detail</th>
              <th class="px-4 py-3 text-center border border-gray-300">Status</th>
              <th
                v-if="can('write permohonan beasiswa')"
                class="px-4 py-3 text-center border border-gray-300"
              >
                Aksi
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="application in reports.data"
              :key="application.id"
              class="even:bg-gray-50 hover:bg-gray-100 focus-within:bg-gray-100"
            >
              <td class="p-0 align-top border border-gray-300">
                <Link
                  :href="`/accountability-reports/${application.id}`"
                  class="block w-full h-full transition-colors duration-150 hover:bg-gray-50"
                >
                  <div class="divide-y divide-gray-200">
                    <div class="p-3">
                      <p class="text-sm font-semibold text-indigo-700">
                        Semester : {{ application.semester }}
                      </p>
                      <p class="text-xs text-gray-600 mt-0.5">
                        Tgl. Pelaporan: <span class="font-medium text-gray-800">{{ application.tgl_pelaporan }}</span>
                      </p>
                    </div>

                    <div class="p-3 text-xs text-gray-700 bg-white">
                      <table class="w-full border-collapse">
                        <tbody>
                          <tr class="">
                            <td class="w-1/2 py-1 font-semibold text-gray-800">Jenis</td>
                            <td class="py-1 text-gray-700">: {{ application.jenis_pt }} - {{ application.jenis }}</td>
                          </tr>
                          <tr class="">
                            <td class="py-1 font-semibold text-gray-800">Nominal Anggaran</td>
                            <td class="py-1 text-gray-700">: {{ formatCurrency(application.nominal_beasiswa) }}</td>
                          </tr>
                          <tr class="">
                            <td class="py-1 font-semibold text-gray-800">Nominal Disetujui</td>
                            <td class="py-1 text-gray-700">: {{ formatCurrency(application.nominal_disetujui) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Link>
              </td>

              <td class="p-0 align-top border border-gray-300">
                <Link
                  :href="`/accountability-reports/${application.id}`"
                  class="block w-full h-full transition-colors duration-150 hover:bg-gray-50"
                >
                  <div class="p-3 text-xs text-gray-700">
                    <table class="w-full border-collapse">
                      <tbody>
                        <tr class="">
                          <td class="w-1/2 py-1 font-semibold text-gray-800">IPK</td>
                          <td class="py-1 text-gray-700">: {{ application.ipk }}</td>
                        </tr>
                        <tr class="">
                          <td class="py-1 font-semibold text-gray-800">No. Rekening</td>
                          <td class="py-1 text-gray-700">: {{ application.account_number }}</td>
                        </tr>
                        <tr class="">
                          <td class="py-1 font-semibold text-gray-800">Biaya Pendidikan</td>
                          <td class="py-1 text-gray-700">: {{ formatCurrency(application.tuition_fee) }}</td>
                        </tr>
                        <tr class="">
                          <td class="py-1 font-semibold text-gray-800">Biaya Lainnya</td>
                          <td class="py-1 text-gray-700">: {{ formatCurrency(application.other_fee) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Link>
              </td>

              <td class="p-3 text-center border border-gray-300">
                <span
                  v-if="application.status == 0"
                  class="px-3 py-1 text-xs bg-yellow-300 rounded"
                >Draft</span>
                <span
                  v-else-if="application.status == 1"
                  class="px-3 py-1 text-xs bg-yellow-500 rounded"
                >Pemberkasan</span>
                <span
                  v-else-if="application.status == 2"
                  class="px-3 py-1 text-xs bg-red-300 rounded"
                >Belum Memenuhi</span>
                <span
                  v-else-if="application.status == 3"
                  class="px-3 py-1 text-xs text-white bg-red-500 rounded"
                >Tidak Memenuhi</span>
                <span
                  v-else-if="application.status == 4"
                  class="px-3 py-1 text-xs bg-green-400 rounded"
                >RAB OK</span>
                <span
                  v-else-if="application.status == 5"
                  class="px-3 py-1 text-xs bg-yellow-400 rounded"
                >PJ Diajukan</span>
                <span
                  v-else-if="application.status == 6"
                  class="px-3 py-1 text-xs bg-orange-400 rounded"
                >Revisi</span>
                <span
                  v-else-if="application.status == 7"
                  class="px-3 py-1 text-xs text-white bg-green-800 rounded"
                >PJ Disetujui</span>
                <p
                  v-if="application.status == 2 || application.status == 3 || application.status == 6"
                  class="mt-2 text-xs text-gray-600"
                >
                  {{ application.catatan_verifikasi }}
                </p>
              </td>
              <td
                v-if="can('write permohonan beasiswa')"
                class="p-3 text-center border border-gray-300"
              >
                <div class="flex justify-center gap-2">
                  <Link
                    v-if="application.status == 0 || application.status == 1 || application.status == 2|| application.status == 6"
                    class="btn-indigo-sm"
                    :href="`/accountability-reports/${application.id}/edit`"
                    title="Ubah"
                  >
                    <font-awesome-icon icon="fas fa-pencil" />
                  </Link>
                  <Link
                    class="btn-green-sm"
                    :href="`/accountability-reports/${application.id}`"
                    title="Lihat"
                  >
                    <font-awesome-icon icon="fas fa-magnifying-glass" />
                  </Link>
                  <Link
                    v-if="application.status == 0"
                    class="btn-red-sm"
                    title="Hapus"
                    @click.prevent="destroy(application.id)"
                  >
                    <font-awesome-icon icon="fas fa-trash" />
                  </Link>
                </div>
              </td>
            </tr>
            <tr v-if="reports.data.length === 0">
              <td
                class="px-6 py-4 text-center text-gray-600 border border-gray-300"
                :colspan="can('write permohonan beasiswa') ? 4 : 3"
              >
                Tidak ada data pertanggung jawaban.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- MOBILE CARD -->
      <div class="p-3 space-y-3 sm:hidden">
        <div
          v-for="application in reports.data"
          :key="application.id"
          class="bg-white border border-gray-300 rounded-lg even:bg-gray-50"
        >
          <div class="flex justify-between p-3 bg-gray-100 border-b border-gray-300 rounded-t-lg">
            <div>
              <p class="text-xs font-semibold">Tgl. Pelaporan: {{ application.tgl_pelaporan }}</p>
            </div>

            <!-- STATUS RESPONSIVE -->
            <span
              v-if="application.status == 0"
              class="items-center justify-center px-2 py-1 text-[11px] font-medium bg-yellow-300 rounded text-center max-w-[100px]"
            >Draft</span>
            <span
              v-else-if="application.status == 1"
              class=" items-center justify-center px-2 py-1 text-[11px] font-medium bg-yellow-500 rounded text-center max-w-[100px]"
            >Pemberkasan</span>
            <span
              v-else-if="application.status == 2"
              class="bg-red-300 px-2 py-0.5 text-[11px]  text-white rounded"
            >Belum Memenuhi</span>
            <span
              v-else-if="application.status == 3"
              class="text-white bg-red-500 px-2 py-0.5 text-[11px] rounded"
            >Tidak Memenuhi</span>
            <span
              v-else-if="application.status == 4"
              class="bg-green-400 px-2 py-0.5 text-[11px] text-white rounded"
            >RAB OK</span>
            <span
              v-else-if="application.status == 5"
              class="bg-yellow-400 px-2 py-0.5 text-[11px] text-white rounded"
            >PJ Diajukan</span>
            <span
              v-else-if="application.status == 6"
              class="bg-orange-400 px-2 py-0.5 text-[11px] text-white rounded"
            >Revisi</span>
            <span
              v-else-if="application.status == 7"
              class="text-white bg-green-500 px-2 py-0.5 text-[11px] rounded"
            >PJ Disetujui</span>
          </div>

          <div class="grid grid-cols-1 text-sm border-t border-gray-300">
            <div class="p-3 border-b border-gray-300">
              <strong>Jenis:</strong> {{ application.jenis_pt }} - {{ application.jenis }}
            </div>
            <div class="p-3 border-b border-gray-300">
              <strong>Nominal Anggaran:</strong> {{ formatCurrency(application.nominal_beasiswa) }}
            </div>
            <div class="p-3 border-b border-gray-300">
              <strong>Nominal Disetujui:</strong> {{ formatCurrency(application.nominal_disetujui) }}
            </div>
            <div class="p-3 border-b border-gray-300">
              <strong>IPK:</strong> {{ application.ipk }}
            </div>
            <div class="p-3 border-b border-gray-300">
              <strong>Semester:</strong> {{ application.semester }}
            </div>
            <div class="p-3 border-b border-gray-300">
              <strong>No. Rekening:</strong> {{ application.account_number }}
            </div>
            <div class="p-3 border-b border-gray-300">
              <strong>Biaya Pendidikan:</strong> {{ formatCurrency(application.tuition_fee) }}
            </div>
            <div class="p-3">
              <strong>Biaya Lainnya:</strong> {{ formatCurrency(application.other_fee) }}
            </div>
          </div>

          <div
            v-if="application.catatan_verifikasi && (application.status == 2 || application.status == 3 || application.status == 6)"
            class="p-3 text-xs text-red-600 border-t border-gray-300 bg-red-50"
          >
            <strong>Catatan:</strong> {{ application.catatan_verifikasi }}
          </div>

          <div
            v-if="can('write permohonan beasiswa')"
            class="flex justify-end gap-2 p-2 border-t border-gray-300 rounded-b-lg bg-gray-50"
          >
            <Link
              v-if="application.status == 0 || application.status == 1 || application.status == 2 || application.status == 6"
              class="btn-indigo-sm"
              :href="`/accountability-reports/${application.id}/edit`"
            >
              <font-awesome-icon icon="fas fa-pencil" />
            </Link>
            <Link
              class="btn-green-sm"
              :href="`/accountability-reports/${application.id}`"
            >
              <font-awesome-icon icon="fas fa-magnifying-glass" />
            </Link>
            <Link
              v-if="application.status == 0"
              class="btn-red-sm"
              @click.prevent="destroy(application.id)"
            >
              <font-awesome-icon icon="fas fa-trash" />
            </Link>
          </div>
        </div>

        <div
          v-if="reports.data.length === 0"
          class="p-3 text-sm text-center text-gray-600 bg-white border border-gray-300 rounded-md"
        >
          Tidak ada data pertanggung jawaban.
        </div>
      </div>
    </div>

    <pagination class="mt-6" :links="reports.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'

export default {
  components: { Head, Link, Pagination },
  layout: Layout,
  props: { reports: Object },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined) return 'Rp 0'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    destroy(id) {
      this.$swal.fire({
        title: 'Anda yakin hapus data ini?',
        text: 'Anda tidak akan dapat mengembalikan data yang sudah dihapus!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) this.$inertia.delete(`/accountability-reports/${id}`)
      })
    },
  },
}
</script>
